import { FeatureCollection, Feature, Geometry } from "geojson"
import { BBox2D } from "./BBox2D";

// https://github.com/whosonfirst/whosonfirst-placetypes
export type PeliasLayer 
    = "venue" 
    | "address" 
    | "street"
    | "intersection"
    | "postalcode" 
    | "locality" 
    | "macrohood"
    | "neighbourhood" 
    | "county" 
    | "localadmin" 
    | "region" 
    | "macrocounty" 
    | "country" 
    | "macroregion" 
    | "dependency" 
    | "borough" 
    | "macrohood" 
    | "marinearea" 
    | "disputed" 
    | "empire" 
    | "continent" 
    | "ocean"
    | "coarse";

/**
* GeoJSON spec: https://geojson.org/
* Coordinates are specified as [ longitude, latitude ] pairs
* Bounding boxes are specified as four element arrays following the pattern [ minimum_longitude, minimum_latitude, maximum_longitude, maximum_latitude]
*/
export interface PeliasFeatureCollection extends FeatureCollection<Geometry, PeliasProperties> {
    bbox?: BBox2D
    geocoding: Geocoding
}

export interface PeliasFeature extends Feature<Geometry, PeliasProperties> {
    bbox?: BBox2D
}

interface ParsedText {
    subject: string;
    housenumber?: string;
    street?: string;
    locality?: string;
    country?: string;
    admin?: string;
}

interface Lang {
    name: string;
    iso6391: string;
    iso6393: string;
    via: string;
    defaulted: boolean;
}

interface Query {
    text: string;
    parser: string;
    parsed_text: ParsedText;
    size: number;
    layers: PeliasLayer[];
    private: boolean;
    "boundary.country"?: string[];
    lang: Lang;
    querySize: number;
}

interface Engine {
    name: string;
    author: string;
    version: string;
}

interface Geocoding {
    version: string;
    attribution: string;
    warnings?: string[];
    errors?: string[];
    query: Query;
    engine: Engine;
    timestamp: number;
}

/**
* This is by no means authoritative
* Response format documenation is lacking: https://geocode.earth/docs/reference/response_format/
* Comments copied from data layers: https://geocode.earth/docs/reference/data_layers/
* Generated using http://json2ts.com/ over responses a few times and diffing results
*/
export interface PeliasProperties {
    id: string;
    gid: string;

    /**
    * The name of the record, plus relevant administrative information. 
    * This data will be formatted according to local rules, for example, as it might be written when addressing a letter.
    */
    layer: PeliasLayer;

    /**
    * The name of the data source this record is from
    * data sources: https://geocode.earth/docs/reference/data_sources/
    */
    source: string;
    source_id: string;

    label: string;

    /**
    * The name of the record itself. 
    * This might be the name of a POI, the country name, or a housenumber and street for an address.
    */
    name: string;

    /**
    * The result’s housenumber, separate from the rest of the address
    */
    housenumber?: string;

    /**
    * Point records include addresses and POIs that generally can be well represented by a single coordinate.
    * Centroid records are generally administrative areas that represent larger areas, and a single coordinate (the centroid) has been chosen to best estimate its location.
    */
    accuracy: "point" | "centroid";

    /**
    * The match_type field describes how well the result type correlates with the type of record Geocode Earth determined you were looking for, based on the input query.
    * For example, a query for an address that returned an address will have a match_type of exact. If instead an interpolated address was returned, the match_type will be interpolated.
    * Finally, if the type of returned record is different from specified in the query, the match_type will be fallback. This will be the case when a query for an address returns a street or city.
    * The match_type property is present only on results from the search and structured search.
    */
    match_type?: string

    /**
    * The result’s street, separate from the rest of the address
    * Streets include any named public or private areas used for travel.
    * This includes highways & roads for vehicles plus some named pathways for bicycle or pedestrian traffic.
    */
    street?: string;

    /**
    * The postal code for the result, such as a ZIP code in the USA
    */
    postalcode?: string;

    /**
    * The ISO_3166-1 (alpha-2) two letter country code
    */
    country_code?: string;
    country_gid?: string;
    
    country_a?: string;
    country?: string;

    macrocounty?: string;
    macrocounty_gid?: string;

    /**
    * Regions are generally the highest level of subdivision within a country.
    * These include states in the United States of America and Australia, provinces in Canada and China, departments in France, etc.
    */
    region?: string;
    region_gid?: string;
    region_a?: string;

    macroregion?: string;
    macroregion_gid?: string;

    /**
    * Locality records refer to any populated place and is the most common way to represent cities across the world.
    */
    locality?: string;
    locality_gid?: string;

    /**
    * Boroughs represent the first level division of a city where an official level of government exists.
    * In the United States the only examples are the five boroughs of New York City and the “Sides” of Chicago.
    * Internationally it’s common to find Boroughs in old European cities such as the twelve boroughs of Berlin where historically many smaller self-governed towns merged but maintained some local government function such as a town hall.
    */
    borough?: string;
    borough_gid?: string;

    /**
    * Neighbourhoods, unlike most other administrative layers, do not generally correspond to an officially recognized boundary (except in Australia, where they represent suburbs.
    * There is often no consensus on the extent of neighbourhoods and their boundaries are often approximate.
    * Geocode Earth always uses the British English spelling of neighbourhood.
    */
    neighbourhood?: string;
    neighbourhood_gid?: string;

    /**
    * Continents are large landmasses or groups of landmasses.
    */
    continent?: string;
    continent_gid?: string;

    /**
    * Counties generally represent the second highest level of subdivision within a country.
    * Note that this term has different meanings in different cultures.
    * We use the USA interpretation but also include the Boroughs of Alaska and Parishes of Louisiana as they perform the same function.
    */
    county?: string;
    county_gid?: string;
    county_a?: string;

    /**
    * Localadmin records generally represent the most local form of government in a given country. 
    * In most places, the localadmin and locality are interchangeable.
    * In the few exceptions, the localadmin generally encompasses several smaller, autonomous communities represented by locality records.
    * When looking for cities, if in doubt, chose locality over localadmin.
    */
    localadmin?: string;
    localadmin_gid?: string;

    addendum?: any
}

export const PeliasFeature = {

    /**
     * @returns a comma delimited concatenation of properties matching the layers if they exist in the
     *          properties.label, otherwise, if no properties match the fallback is properties.name
     */
    joinProperties: (feature: Partial<PeliasFeature> | undefined, layers: PeliasLayer[], delim?: string) => {
        const properties = feature?.properties;

        if (!properties) {
            return '';
        }

        return layers?.reduce((text, layer) => {
            if (properties.layer === layer) {
                return properties.name;
            }

            const value = (properties as any)[layer];

            if (!value) {
                return text;
            }
            if (!text) {
                return value;
            }
            if (properties.label.indexOf(value) === -1) {
                return text
            } 
            if (text.indexOf(value) === -1) {
                return text + (delim || ', ') + value;
            }
            return text;
        }, '');
    },

    findGid: (feature: Partial<PeliasFeature> | undefined, layers: PeliasLayer[]) => {
        const properties = feature?.properties;
        if (!properties) {
            return undefined;
        }

        let result: string | undefined = undefined;

        layers.every(layer => {
            result = properties.layer === layer ? properties.gid : (properties as any)[layer + '_gid'] as string;
            return !result;
        });

        return result;
    },

    toBBox: (feature: Partial<PeliasFeature> | undefined) : BBox2D | undefined => {
        if (!feature) {
            return undefined;
        }
        
        const { geometry, properties, bbox } = feature;

        if (bbox && properties?.accuracy === "centroid") {
            return feature.bbox;
        }
        
        if (geometry?.type === "Point") {
            const [ lon, lat ] = geometry.coordinates;
            return [ lon, lat, lon, lat ];
        }

        return undefined;
    }
}